import React from 'react';
import { Helmet, flattenToAppURL } from '@plone/volto/helpers';
import { useSiteConfig } from '../../helpers/useSiteConfig';
import {
  PUBLISHER_NAME,
  PUBLISHER_LEGAL_NAME,
} from '../../siteConfig/branding';
import { extractRelations } from '../DocumentView/relations';

/**
 * Per-document SEO head: title, meta description, canonical, OpenGraph/Twitter,
 * and Schema.org JSON-LD. SEO is a first-class requirement for the archive, so
 * every document exposes structured metadata a crawler can index.
 *
 * Absolute URLs: canonical/og:url/JSON-LD `url` are built from the site's public
 * origin, never left site-relative. Google supports relative canonicals but
 * explicitly recommends absolute ones, and a relative og:url is simply wrong for
 * crawlers and social cards. The origin comes from SiteConfig.baseUrl so the
 * value is identical on the server and the client and does not depend on the
 * request host (which is what made the first audit of this page misleading).
 */

/** Schema.org type per Plone portal_type.
 *
 * These are policy instruments with a legal force status, so documents get a
 * dual type: `DigitalDocument` keeps the generic document semantics while
 * `Legislation` contributes `legislationIdentifier` and `legislationLegalForce`
 * — the latter maps almost exactly onto our Current / Archive / Rescinded
 * vocabulary. A Person is emphatically not a DigitalDocument, which is what the
 * previous single hardcoded type made every one of them.
 */
const DOCUMENT_TYPES = [
  'Standard',
  'Directive',
  'Policy',
  'OtherPolicy',
  'Charter',
  'Delegation',
];

const schemaTypeFor = (portalType) => {
  if (portalType === 'Person') return 'Person';
  if (portalType === 'Definition' || portalType === 'Acronym')
    return 'DefinedTerm';
  if (DOCUMENT_TYPES.includes(portalType))
    return ['DigitalDocument', 'Legislation'];
  return 'DigitalDocument';
};

/** schema.org/LegalForceStatus — only emitted when the mapping is unambiguous. */
const LEGAL_FORCE = {
  Current: 'https://schema.org/InForce',
  Archive: 'https://schema.org/NotInForce',
  Archived: 'https://schema.org/NotInForce',
  Rescinded: 'https://schema.org/NotInForce',
  Canceled: 'https://schema.org/NotInForce',
  Cancelled: 'https://schema.org/NotInForce',
};

/** Human labels per portal_type, for the composed description fallback. */
const TYPE_LABEL = {
  Standard: 'technical standard',
  Directive: 'DOE directive',
  Policy: 'policy',
  OtherPolicy: 'policy document',
  Charter: 'charter',
  Delegation: 'delegation of authority',
  Definition: 'glossary definition',
  Acronym: 'acronym',
  Person: 'person',
  Project: 'project',
};

/**
 * Put the document's identifier in front of its title.
 *
 * This is the whole of BL-0026, and it is the one change on this page that is
 * backed by a live controlled experiment rather than argument. On 2026-08-13
 * the archive ranked on page one for `426.2a current` — with the VERSION
 * HISTORY page, not the document. For the exact identifier query
 * `"DOE O 426.2A Chg 1"` our version-history page ranked 2nd and two glossary
 * pages 4th and 8th, while the document page itself did not rank at all. A
 * `site:` query proves the document page IS indexed, so crawling, indexing and
 * demand are all eliminated as explanations. The only difference between the
 * pages that ranked and the page that did not: the identifier is in their
 * `<title>` and was missing from its. It reproduces on standards with
 * `"DOE-STD-1153-2019"`.
 *
 * The audience searches by number — 14 of the top 18 real on-site search terms
 * are document identifiers (GA4, and see BL-0044 for why that report needed
 * cleaning before it could be read). The identifier is also already the first
 * thing in the page's own `<h1>`, so this makes the title agree with the
 * heading rather than inventing anything.
 *
 * `startsWith` guards the documents whose stored title already leads with the
 * identifier — mostly news and a few migrated items — so they do not end up
 * with it twice.
 */
export const titleWithDocid = (docid, title) => {
  if (!docid || !title) return title || docid || '';
  return title.startsWith(docid) ? title : `${docid}, ${title}`;
};

/**
 * Tidy a stored description into something that reads as a sentence in a
 * result list.
 *
 * The stored Plone `description` is authoritative and stays so — this does not
 * replace it, rewrite it, or second-guess its content. It fixes presentation
 * defects that come from the migration, where some descriptions were taken
 * from the head of the document body and kept its formatting:
 *
 *   - literal tabs and newlines, which render as stray whitespace in a SERP;
 *   - a leading clause enumerator, e.g. `(a.)\tThis Order establishes …`,
 *     which is the exact snippet Google was showing for DOE O 426.2A;
 *   - paragraph-length text, when a snippet is what is wanted.
 *
 * Anything that ends up too short to be a useful sentence is treated as absent
 * and falls through to the composed description instead. Measured 2026-08-14:
 * 1,326 of 1,444 published documents have a description that is already fine,
 * so this is a repair for the tail, not a rewrite of the corpus. The remaining
 * empty and stub descriptions are a DATA fix, tracked separately.
 */
const LEADING_ENUMERATOR = /^\(\s*[A-Za-z0-9]{1,4}\s*\.?\s*\)\s*|^\d{1,2}\.\s+/;
// BL-0157 (TAIUIT seo/meta-description): Google shows about 160 characters of a
// meta description and the scanner flags anything longer. The ellipsis counts,
// so the cut below is taken at MAX_DESCRIPTION - 1.
const MAX_DESCRIPTION = 160;
const MIN_USEFUL_DESCRIPTION = 50;

/**
 * The presentation repair WITHOUT the length cut.
 *
 * Split out of `cleanDescription` because the 160-character cut is a SERP
 * constraint and nothing else. `<meta name="description">` is rendered into a
 * snippet and wants a snippet; schema.org `description` has no length limit and
 * is read whole by rich-result parsers and by the AI crawlers that are now a
 * large share of our traffic. Feeding both from the truncated string withheld
 * 196,748 characters across 1,062 published documents -- 61.8% of standards
 * (368/595), 49.4% of policies, 33.5% of directives -- each ending in an
 * ellipsis with the full text visible in the DOM directly below it
 * [measured 2026-09-01 over the published corpus via @search].
 */
export const fullDescription = (raw) => {
  if (!raw) return undefined;
  const text = String(raw)
    .replace(/\s+/g, ' ')
    .trim()
    .replace(LEADING_ENUMERATOR, '')
    .trim();
  return text.length < MIN_USEFUL_DESCRIPTION ? undefined : text;
};

export const truncateDescription = (text) => {
  if (!text || text.length <= MAX_DESCRIPTION) return text;
  const cut = text.slice(0, MAX_DESCRIPTION - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).replace(
    /[,;:.\s]+$/,
    '',
  )}\u2026`;
};

export const cleanDescription = (raw) => truncateDescription(fullDescription(raw));

/**
 * A glossary term's own definitions, for the machine-readable payload.
 *
 * Definition objects carry up to five numbered slots, because different source
 * directives define the same term differently. Their stored Plone
 * `description` is a copy of the first one CAPPED AT 280 CHARACTERS BY THE
 * MIGRATION: 943 of 2,564 published terms already end in an ellipsis before
 * this component ever sees them, and `cleanDescription` only shortens, so it
 * cannot repair them for schema.org [measured 2026-09-01]. COGCON is the worked
 * example -- 279 stored characters against 435 in `definition_1`.
 *
 * Reading the fields the page already renders fixes it in the frontend and
 * touches no stored data.
 */
const DEFINITION_FIELDS = [
  'definition_1',
  'definition_2',
  'definition_3',
  'definition_4',
  'definition_5',
];

/** `&amp;` is decoded LAST so `&amp;lt;` cannot become a `<`. */
export const stripHtml = (value) => {
  const raw = typeof value === 'string' ? value : value && value.data;
  if (!raw) return '';
  return String(raw)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&(?:apos|#0*39);/gi, "'")
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
};

export const definitionText = (content) => {
  const parts = DEFINITION_FIELDS.map((f) => stripHtml(content[f])).filter(
    Boolean,
  );
  if (!parts.length) return undefined;
  const text =
    parts.length === 1
      ? parts[0]
      : parts.map((p, i) => `${i + 1}. ${p}`).join(' ');
  return text.length < MIN_USEFUL_DESCRIPTION ? undefined : text;
};

/** subject_areas may arrive as plain strings or as {token,title} vocab items. */
const toKeywords = (value) => {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  const out = list
    .map((v) => (typeof v === 'string' ? v : v && (v.title || v.token)))
    .filter(Boolean);
  return out.length ? out : undefined;
};

const DocumentSEO = ({ content }) => {
  const site = useSiteConfig(content);
  if (!content) return null;

  const path = flattenToAppURL(content['@id'] || '');
  const origin = (site.baseUrl || '').replace(/\/+$/, '');
  const href = origin
    ? `${origin}${path.startsWith('/') ? '' : '/'}${path}`
    : path;
  const status =
    content.status && (content.status.title || content.status.token);
  const docid = content.docid;
  const portalType = content['@type'];
  const schemaType = schemaTypeFor(portalType);
  const isDocument = Array.isArray(schemaType);
  const isPerson = schemaType === 'Person';
  const isDefinedTerm = schemaType === 'DefinedTerm';

  // The document's own date. All three are AUTHORITATIVE per dates.md; the
  // type-specific two exist because Delegations and Charters carry no
  // `issue_date` at all -- a Delegation's real date is its
  // `authority_effective_date`, a Charter's is `date_signed`. Without them the
  // 55 nnsa documents with no issue_date emit no date rather than the correct
  // one [measured 2026-08-03].
  const ownDate =
    content.issue_date ||
    content.authority_effective_date ||
    content.date_signed ||
    undefined;

  // The heading the page already shows, plus the archive name. `seoName`
  // rather than `title`, so the suffix names the corpus the document belongs
  // to rather than a two-word brand that says nothing to a searcher.
  const headline = titleWithDocid(docid, content.title);
  const title = `${headline} | ${site.seoName}`;

  /**
   * The stored Plone `description` stays authoritative wherever it exists —
   * measured 2026-08-14, 1,326 of 1,444 published documents have a good one,
   * so this is a fallback for the ~118 that do not, not a sweep. (An earlier
   * report of mine claimed the descriptions were raw body excerpts corpus-wide.
   * That was over-generalised from a single example and is wrong: nothing in
   * this addon excerpts a body, so the bad ones are a DATA problem, tracked
   * separately.)
   *
   * When there is no description, Google composes its own snippet from the
   * page. Giving it a written sentence built from the fields we are confident
   * in — identifier, type, status, date — beats whatever it picks, and beats
   * the old fallback, which was the title again followed by the site tagline
   * and told a reader nothing they could not already see in the title.
   */
  const composedDescription = () => {
    const typeLabel = TYPE_LABEL[portalType] || 'document';
    const year = ownDate ? String(ownDate).slice(0, 4) : undefined;
    const lead = docid ? `${docid} — ${content.title}` : content.title;
    const facts = [
      typeLabel,
      status ? status.toLowerCase() : undefined,
      year && /^\d{4}$/.test(year) ? `dated ${year}` : undefined,
    ].filter(Boolean);
    return `${lead}. A ${facts.join(', ')}, held in ${site.title}.`;
  };

  // BL-0157: the composed fallback is capped like a stored description. A
  // long title made it run past 160, which is past where Google cuts the
  // snippet and what TAIUIT flags.
  const description =
    cleanDescription(content.description) ||
    truncateDescription(composedDescription());

  /**
   * schema.org `description` is NOT the meta description, and is built
   * separately: a glossary term's own definition fields where they exist,
   * otherwise the untruncated stored description, falling back to the same
   * composed sentence the meta tag uses when nothing is stored. The meta,
   * og and twitter tags above keep the truncated `description` unchanged.
   */
  const structuredDescription =
    (isDefinedTerm && definitionText(content)) ||
    fullDescription(content.description) ||
    composedDescription();

  const jsonld = {
    '@context': 'https://schema.org',
    '@type': schemaType,
    name: content.title,
    description: structuredDescription,
    url: href,
    // Only the document's OWN dates are exported. `modified`, `effective` and
    // `created` are Plone bookkeeping -- filled on 100% of objects, so they
    // always win a fallback, and they carry our import timestamp rather than
    // anything DOE asserted. Emitting `modified` told Google that 6,233
    // published documents were all revised in 2026, the year we migrated them
    // [measured 2026-08-03]. Absent is correct when we do not know.
    // See prc-maintenance/content-model/dates.md and RULES.md rule 8.
    dateModified: content.updated_date || ownDate,
    datePublished: ownDate,
    isPartOf: { '@type': 'Collection', name: site.seoName },
    // BL-0082: name the operator in structured data too, so the
    // non-affiliation is machine-readable and not only prose in the footer.
    // Google attributes the page to this Organization rather than inferring
    // a government publisher from the corpus.
    publisher: {
      '@type': 'Organization',
      name: PUBLISHER_LEGAL_NAME,
      url: 'https://www.doxcelerate.com/',
    },
  };

  if (!isPerson) {
    jsonld.identifier = docid || undefined;
    jsonld.creativeWorkStatus = status || undefined;
    jsonld.keywords = toKeywords(content.subject_areas);
  }
  if (isDocument) {
    // Legislation-specific fields: the document number and whether it is in force.
    jsonld.legislationIdentifier = docid || undefined;
    jsonld.legislationLegalForce = (status && LEGAL_FORCE[status]) || undefined;

    // Version family, from the backend document_relations graph. A newer
    // version is DERIVED FROM the documents it replaced -> schema.org
    // `isBasedOn`; the reverse edge and lateral relations are references to
    // other works -> `citation`. Deliberately NOT `sameAs` and NOT a shared
    // canonical: each version is a distinct, separately indexable page, and
    // collapsing them would erase exactly the history this archive exists to
    // keep. Absolute URLs, document types only.
    const relations = extractRelations(content, site);
    const basedOn = relations.supersedes.map((r) => r.abs);
    const cites = [...relations.supersededBy, ...relations.related].map(
      (r) => r.abs,
    );
    if (basedOn.length) jsonld.isBasedOn = basedOn;
    if (cites.length) jsonld.citation = cites;
  }

  // Structured-data task (2026-09-27): also considered `isBasedOn`/`sameAs`
  // pointing at the document's official DOE source record
  // (directives.energy.gov, www.standards.doe.gov, directives.nnsa.energy.gov)
  // -- SKIPPED on all three sites, not implemented here:
  //
  //   - No Directive, Standard or nnsa Policy object carries a source-URL
  //     field. Confirmed by fetching one live object of each type from the
  //     backend and listing its keys [curl 127.0.0.1:9080, 2026-09-27] --
  //     zero hits for directives.energy.gov / www.standards.doe.gov /
  //     directives.nnsa.energy.gov anywhere in the fields, including
  //     full_text_sections (whose only match is the generic, non-docid
  //     boilerplate string "https://www.directives.doe.gov").
  //   - The committed crosswalk (prc-maintenance/currency/doe/) that DOES
  //     carry a per-document DOE url is a point-in-time snapshot, last run
  //     2026-08-07 (51 days stale as of this writing), and it is never
  //     joined onto the Plone content objects or exposed through any API
  //     this component can read at request time.
  //   - A fresher, already-joined mapping exists at watch/data/{standards,
  //     directives}.json (last_run 2026-09-27: 593 and 1971 records, each
  //     carrying a `url` keyed to `prc_path`) but watch/ is explicitly
  //     uncommitted work the owner is holding -- out of bounds for this
  //     change. That data is the ready next step once the owner authorizes
  //     reading it from production code; until then, do NOT construct a URL
  //     by guessing a pattern.
  //   - NNSA has no stable per-document URL at all, in any source: its
  //     Appian portal serves PDFs through a stateful multi-step POST flow
  //     keyed to ephemeral context/uuid/_cId tokens (prc-maintenance/
  //     currency/doe/01-sources.md), and even the fresh watch/data/nnsa.json
  //     sample shows `url: null`. There is nothing to link to, independent
  //     of the freshness/authorization question above.

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={href} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={headline} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={PUBLISHER_NAME} />
      <meta property="og:url" content={href} />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={headline} />
      <meta name="twitter:description" content={description} />
      <script type="application/ld+json">{JSON.stringify(jsonld)}</script>
    </Helmet>
  );
};

export default DocumentSEO;
